import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import { DEFAULT_LANG, LANG_STORAGE_KEY, SUPPORTED_LANGS, getStoredLang } from '@/lib/i18n';

import jaCommon from './locales/ja/common.json';
import jaMasters from './locales/ja/masters.json';
import jaBatches from './locales/ja/batches.json';
import jaImports from './locales/ja/imports.json';
import jaOperations from './locales/ja/operations.json';
import jaMassBalance from './locales/ja/mass-balance.json';
import jaExceptions from './locales/ja/exceptions.json';
import jaAuditPackage from './locales/ja/audit-package.json';
import jaEvidence from './locales/ja/evidence.json';
import jaApprovals from './locales/ja/approvals.json';
import jaPricing from './locales/ja/pricing.json';
import jaSettings from './locales/ja/settings.json';
import jaFirstValuation from './locales/ja/firstValuation.json';
import jaValuation from './locales/ja/valuation.json';
import jaSettlement from './locales/ja/settlement.json';
import jaReports from './locales/ja/reports.json';
import jaFieldCapture from './locales/ja/fieldCapture.json';
import jaDisassembly from './locales/ja/disassembly.json';
import jaSales from './locales/ja/sales.json';
import jaReclassified from './locales/ja/reclassified.json';
import jaCorrections from './locales/ja/corrections.json';
import jaPortal from './locales/ja/portal.json';
import jaFileLibrary from './locales/ja/fileLibrary.json';
import jaIssues from './locales/ja/issues.json';

import zhCommon from './locales/zh-TW/common.json';
import zhMasters from './locales/zh-TW/masters.json';
import zhBatches from './locales/zh-TW/batches.json';
import zhImports from './locales/zh-TW/imports.json';
import zhOperations from './locales/zh-TW/operations.json';
import zhMassBalance from './locales/zh-TW/mass-balance.json';
import zhExceptions from './locales/zh-TW/exceptions.json';
import zhAuditPackage from './locales/zh-TW/audit-package.json';
import zhEvidence from './locales/zh-TW/evidence.json';
import zhApprovals from './locales/zh-TW/approvals.json';
import zhPricing from './locales/zh-TW/pricing.json';
import zhSettings from './locales/zh-TW/settings.json';
import zhFirstValuation from './locales/zh-TW/firstValuation.json';
import zhValuation from './locales/zh-TW/valuation.json';
import zhSettlement from './locales/zh-TW/settlement.json';
import zhReports from './locales/zh-TW/reports.json';
import zhFieldCapture from './locales/zh-TW/fieldCapture.json';
import zhDisassembly from './locales/zh-TW/disassembly.json';
import zhSales from './locales/zh-TW/sales.json';
import zhReclassified from './locales/zh-TW/reclassified.json';
import zhCorrections from './locales/zh-TW/corrections.json';
import zhPortal from './locales/zh-TW/portal.json';
import zhFileLibrary from './locales/zh-TW/fileLibrary.json';
import zhIssues from './locales/zh-TW/issues.json';

import enCommon from './locales/en/common.json';
import enMasters from './locales/en/masters.json';
import enBatches from './locales/en/batches.json';
import enImports from './locales/en/imports.json';
import enOperations from './locales/en/operations.json';
import enMassBalance from './locales/en/mass-balance.json';
import enExceptions from './locales/en/exceptions.json';
import enAuditPackage from './locales/en/audit-package.json';
import enEvidence from './locales/en/evidence.json';
import enApprovals from './locales/en/approvals.json';
import enPricing from './locales/en/pricing.json';
import enSettings from './locales/en/settings.json';
import enFirstValuation from './locales/en/firstValuation.json';
import enValuation from './locales/en/valuation.json';
import enSettlement from './locales/en/settlement.json';
import enReports from './locales/en/reports.json';
import enFieldCapture from './locales/en/fieldCapture.json';
import enDisassembly from './locales/en/disassembly.json';
import enSales from './locales/en/sales.json';
import enReclassified from './locales/en/reclassified.json';
import enCorrections from './locales/en/corrections.json';
import enPortal from './locales/en/portal.json';
import enFileLibrary from './locales/en/fileLibrary.json';
import enIssues from './locales/en/issues.json';

/**
 * i18next is initialized with all three supported languages: ja (default),
 * zh-TW, and en. Required by ECO FAITH (日方) + 大鑫産業 (台灣方) joint
 * operation. Key convention: `efs.<module>.<screen>.<key>` — see
 * docs/specs/08-pa-jp-design-reference.md §9.
 *
 * Resources are loaded statically (no async backend) for the scaffold;
 * later phases can switch to lazy loading per route.
 *
 * Language is resolved from localStorage (`efs.lang`); falls back to ja
 * when nothing is stored. UI language switcher lives in TopNav.
 */
/**
 * DEV-only: allow `?lng=` to pin the initial language synchronously so
 * headless per-locale captures (spec-13 §16.4) never race an async language
 * switch. Production ignores the parameter entirely.
 */
const devLangOverride =
  import.meta.env.DEV && typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('lng')
    : null;

const initialLang =
  (devLangOverride && (SUPPORTED_LANGS as readonly string[]).includes(devLangOverride)
    ? devLangOverride
    : null) ??
  getStoredLang() ??
  DEFAULT_LANG;

void i18n.use(initReactI18next).init({
  resources: {
    ja: {
      common: jaCommon,
      masters: jaMasters,
      batches: jaBatches,
      imports: jaImports,
      operations: jaOperations,
      'mass-balance': jaMassBalance,
      exceptions: jaExceptions,
      'audit-package': jaAuditPackage,
      evidence: jaEvidence,
      approvals: jaApprovals,
      pricing: jaPricing,
      settings: jaSettings,
      firstValuation: jaFirstValuation,
      valuation: jaValuation,
      settlement: jaSettlement,
      reports: jaReports,
      fieldCapture: jaFieldCapture,
      disassembly: jaDisassembly,
      sales: jaSales,
      reclassified: jaReclassified,
      corrections: jaCorrections,
      portal: jaPortal,
      fileLibrary: jaFileLibrary,
      issues: jaIssues,
    },
    'zh-TW': {
      common: zhCommon,
      masters: zhMasters,
      batches: zhBatches,
      imports: zhImports,
      operations: zhOperations,
      'mass-balance': zhMassBalance,
      exceptions: zhExceptions,
      'audit-package': zhAuditPackage,
      evidence: zhEvidence,
      approvals: zhApprovals,
      pricing: zhPricing,
      settings: zhSettings,
      firstValuation: zhFirstValuation,
      valuation: zhValuation,
      settlement: zhSettlement,
      reports: zhReports,
      fieldCapture: zhFieldCapture,
      disassembly: zhDisassembly,
      sales: zhSales,
      reclassified: zhReclassified,
      corrections: zhCorrections,
      portal: zhPortal,
      fileLibrary: zhFileLibrary,
      issues: zhIssues,
    },
    en: {
      common: enCommon,
      masters: enMasters,
      batches: enBatches,
      imports: enImports,
      operations: enOperations,
      'mass-balance': enMassBalance,
      exceptions: enExceptions,
      'audit-package': enAuditPackage,
      evidence: enEvidence,
      approvals: enApprovals,
      pricing: enPricing,
      settings: enSettings,
      firstValuation: enFirstValuation,
      valuation: enValuation,
      settlement: enSettlement,
      reports: enReports,
      fieldCapture: enFieldCapture,
      disassembly: enDisassembly,
      sales: enSales,
      reclassified: enReclassified,
      corrections: enCorrections,
      portal: enPortal,
      fileLibrary: enFileLibrary,
      issues: enIssues,
    },
  },
  lng: initialLang,
  fallbackLng: DEFAULT_LANG,
  supportedLngs: [...SUPPORTED_LANGS],
  defaultNS: 'common',
  /**
   * If a lookup for `efs.common.x.y` is made from a hook scoped to another
   * namespace (e.g. `useTranslation('operations')`), i18next will not find
   * the key in `operations` and will fall back to look it up in `common`.
   * This avoids having to pass `{ ns: 'common' }` on every shared button.
   */
  fallbackNS: 'common',
  ns: [
    'common',
    'masters',
    'batches',
    'imports',
    'operations',
    'mass-balance',
    'exceptions',
    'audit-package',
    'evidence',
    'approvals',
    'pricing',
    'settings',
    'firstValuation',
    'valuation',
    'settlement',
    'reports',
    'fieldCapture',
    'disassembly',
    'sales',
    'reclassified',
    'corrections',
    'portal',
    'fileLibrary',
    'issues',
  ],
  interpolation: {
    escapeValue: false,
  },
  returnNull: false,
  detection: {
    order: [],
    lookupLocalStorage: LANG_STORAGE_KEY,
    caches: [],
  },
});

export default i18n;
